import { getToken, Messaging } from 'firebase/messaging';
import { messaging } from './firebase';
import { setDeviceFcmToken } from './localstorage-helper';

const VAPID_KEY = process.env.NX_FIREBASE_VAPID_KEY;

/**
 * This browser's FCM token, asking for notification permission first. `null`
 * when push cannot work here: no VAPID key in the build, permission refused,
 * or no token from Firebase.
 *
 * Uses the PWA registration made by `registerAppSw()` in each app's main.tsx -
 * a second script on scope '/' would replace it on every load. In a dev serve
 * there is no worker, so `ready` never settles and neither does this.
 *
 * The token is remembered outside the auth store before anyone registers it:
 * signing out has to send this exact token so the user's OTHER devices keep
 * their push, and by then the store has been cleared. Registering the same
 * token twice is a no-op on the backend, so recording it early is safe.
 */
export const getDevicePushToken = async (): Promise<string | null> => {
  if (!VAPID_KEY) {
    console.error('Missing VAPID key');
    return null;
  }
  if (!messaging || !('serviceWorker' in navigator)) return null;

  const registration = await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('Notification permission not granted');
    return null;
  }

  const token = await getToken(messaging as Messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) return null;

  setDeviceFcmToken(token);
  return token;
};
